import { useCallback, useRef, useEffect } from "react";
import previewCache from "../utils/previewCache";
import supabase from "../supabaseClient";

const SERVER_URL = process.env.REACT_APP_SERVER_URL;

/**
 * Hook for preloading recipe previews
 * Handles async preloading, caching, and request deduplication
 */
export function usePreviewPreloader() {
  const abortControllersRef = useRef(new Map()); // URL -> AbortController

  /**
   * Fetch preview for a single URL
   */
  const fetchPreview = useCallback(async (url, signal = null) => {
    // Check cache first
    const cached = previewCache.get(url);
    if (cached) {
      return { success: true, recipe: cached, fromCache: true };
    }

    // Check if request is already pending
    const pendingRequest = previewCache.getPendingRequest(url);
    if (pendingRequest) {
      try {
        const result = await pendingRequest;
        return { ...result, fromCache: false };
      } catch (error) {
        throw error;
      }
    }

    // Create new request
    const requestPromise = (async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();
        const accessToken = session?.access_token;

        const response = await fetch(`${SERVER_URL}/search/preview-recipe`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
          },
          body: JSON.stringify({ url }),
          signal, // Support cancellation
        });

        const result = await response.json();

        if (response.ok && result.success && result.recipe) {
          // Only cache successful results with recipes
          previewCache.set(url, result.recipe);
          return { success: true, recipe: result.recipe };
        } else {
          // Handle errors gracefully - return error info instead of throwing
          // Don't cache error results - allow retry
          const errorMessage = result.error || "Failed to preview recipe";
          return { 
            success: false, 
            error: errorMessage,
            recipe: null 
          };
        }
      } catch (error) {
        if (error.name === "AbortError") {
          throw error; // Re-throw abort errors
        }
        console.error(`[PreviewPreloader] Error fetching preview for ${url}:`, error);
        // Return error instead of throwing for graceful handling
        return { 
          success: false, 
          error: error.message || "Network error while fetching preview",
          recipe: null 
        };
      }
    })();

    // Store pending request
    previewCache.setPendingRequest(url, requestPromise);

    const result = await requestPromise;
    return { ...result, fromCache: false };
  }, []);

  /**
   * Preload previews for multiple URLs with priority
   * Processes queue with concurrency limit
   */
  const preloadPreviews = useCallback(
    async (urls, options = {}) => {
      const {
        priority = "sequential", // 'sequential' or 'parallel'
        maxConcurrent = 3, // Max concurrent requests
        onProgress = null, // Callback for progress updates
        signal = null, // AbortSignal for cancellation
      } = options;

      if (priority === "parallel") {
        // Process all URLs in parallel (with concurrency limit)
        const results = [];
        const errors = [];

        // Process in batches
        for (let i = 0; i < urls.length; i += maxConcurrent) {
          const batch = urls.slice(i, i + maxConcurrent);
          const batchPromises = batch.map(async (url) => {
            try {
              const result = await fetchPreview(url, signal);
              if (result.success) {
                if (onProgress) {
                  onProgress({ 
                    url, 
                    success: true, 
                    fromCache: result.fromCache,
                    recipe: result.recipe 
                  });
                }
                return { url, success: true, result };
              } else {
                // Handle error result gracefully
                if (onProgress) {
                  onProgress({ url, success: false, error: result.error });
                }
                return { url, success: false, error: result.error };
              }
            } catch (error) {
              if (error.name !== "AbortError") {
                if (onProgress) {
                  onProgress({ url, success: false, error: error.message });
                }
                return { url, success: false, error: error.message };
              }
              return null; // Aborted
            }
          });

          const batchResults = await Promise.allSettled(batchPromises);
          batchResults.forEach((settled) => {
            if (settled.status === "fulfilled" && settled.value) {
              if (settled.value.success) {
                results.push(settled.value);
              } else {
                errors.push(settled.value);
              }
            }
          });

          // Check for cancellation
          if (signal?.aborted) {
            break;
          }
        }

        return { results, errors };
      } else {
        // Sequential processing (one at a time)
        const results = [];
        const errors = [];

        for (const url of urls) {
          if (signal?.aborted) break;

          try {
            const result = await fetchPreview(url, signal);
            if (result.success) {
              if (onProgress) {
                onProgress({ 
                  url, 
                  success: true, 
                  fromCache: result.fromCache,
                  recipe: result.recipe 
                });
              }
              results.push({ url, success: true, result });
            } else {
              // Handle error result gracefully
              if (onProgress) {
                onProgress({ url, success: false, error: result.error });
              }
              errors.push({ url, success: false, error: result.error });
            }
          } catch (error) {
            if (error.name !== "AbortError") {
              if (onProgress) {
                onProgress({ url, success: false, error: error.message });
              }
              errors.push({ url, success: false, error: error.message });
            }
          }
        }

        return { results, errors };
      }
    },
    [fetchPreview]
  );

  /**
   * Cancel all pending requests
   */
  const cancelAll = useCallback(() => {
    abortControllersRef.current.forEach((controller) => {
      controller.abort();
    });
    abortControllersRef.current.clear();
  }, []);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    return () => {
      cancelAll();
    };
  }, [cancelAll]);

  return {
    fetchPreview,
    preloadPreviews,
    cancelAll,
    getCacheStats: () => previewCache.getStats(),
  };
}

